'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface HaalCard {
  num: string;
  arabic: string;
  title: string;
  subtitle: string;
  description: string;
  platform: string;
  color: string;
}

interface RoleCard {
  id: string;
  label: string;
  question: string;
  sequences: string;
  keys: string;
}

// ─── DATA ─────────────────────────────────────────────────────────────────────

const FIVE_HAALS: HaalCard[] = [
  {
    num: '١',
    arabic: 'الحواس',
    title: 'Hawas — Sensing',
    subtitle: 'HAAL 1: SENSING',
    description: 'Perceiving creation through the senses. The read-only state — observing Ayat, signs, before interpretation. Like the first stage of the internet: you navigate and receive.',
    platform: 'Begin in the IRL Lens — select a city, observe a tile',
    color: '#2d8a87',
  },
  {
    num: '٢',
    arabic: 'العلم',
    title: 'Ilm — Knowing',
    subtitle: 'HAAL 2: KNOWING',
    description: 'Gathering information. The Three Questions: Is it? What is it? When is it? Recognizing the Huduth — the essential and temporal nature of what you observe.',
    platform: 'Layer 2 in every IRL tile detail panel',
    color: '#2d7a47',
  },
  {
    num: '٣',
    arabic: 'الفكر',
    title: 'Fikr — Reflecting',
    subtitle: 'HAAL 3: REFLECTING',
    description: 'Deep contemplation. Connecting what you observe to the Names and Attributes of Allah. The Sunnat — the Way of Allah — becomes visible through reflection.',
    platform: 'Layers 3–6: Names Fission, Sunnat, Zahir, Batin',
    color: '#d4a82a',
  },
  {
    num: '٤',
    arabic: 'الحق',
    title: 'Haqq — Reality',
    subtitle: 'HAAL 4: KNOWING THE HAQQ',
    description: 'Recognition of truth, right, and purpose in what you observe. The Mizan — balance — becomes apparent. Injustice becomes identifiable. The obligation of the caretaker becomes clear.',
    platform: 'Layer 7: Haqq statement in every sequence',
    color: '#f5f0e8',
  },
  {
    num: '٥',
    arabic: 'العمل',
    title: 'Amanah — Acting',
    subtitle: 'HAAL 5: ACTING',
    description: 'The trust fulfilled through deed. Linking observation to work — local resources, community quests, bridge-building. Ihsan: acting as though you can see Allah.',
    platform: 'Layer 8: Amanah + Bridges → MRG resources',
    color: '#c9a84c',
  },
];

const ROLES: RoleCard[] = [
  { id: 'engineer', label: 'Engineer', question: 'What tools am I building, and toward what?', sequences: 'Mycorrhizal, Salmon Run', keys: 'MIZAN, AMANAH' },
  { id: 'teacher', label: 'Teacher', question: 'What does the curriculum look like?', sequences: 'Longleaf Pine, Metamorphosis', keys: 'AYAT, TAQWAH' },
  { id: 'scientist', label: 'Scientist', question: 'What breakthrough research motivates me?', sequences: 'Great Oxygenation, Stromatolites', keys: 'AYAT, HAAL' },
  { id: 'gardener', label: 'Gardener', question: 'Which plants am I planting, where, and why?', sequences: 'All ecological sequences', keys: 'AYAT, MIZAN' },
  { id: 'community-organizer', label: 'Community Organizer', question: 'What does decentralized resource management look like?', sequences: 'Mycorrhizal (community networks)', keys: 'HAAL, AMANAH' },
  { id: 'formerly-incarcerated', label: 'Formerly Incarcerated', question: 'What reforms and propositions do I bring?', sequences: 'MRG: Justice-Involved resources', keys: 'AMANAH, TAQWAH' },
  { id: 'student', label: 'Student / Law Student', question: 'What class would this project be for? What routes are available?', sequences: 'Longleaf Pine, Metamorphosis', keys: 'AYAT, HAAL' },
  { id: 'custodian', label: 'Custodian / Janitor', question: 'How would a janitor approach custodianship of endangered habitats?', sequences: 'Decomposition sequences', keys: 'MIZAN, AMANAH' },
];

const ROLE_QUEST_FOCUS: Record<string, string> = {
  gardener: 'Focus on: root systems, soil life, water movement, light patterns, seasonal indicators',
  teacher: 'Focus on: how knowledge passes between organisms, who teaches, who learns, what changes',
  engineer: 'Focus on: structural systems, load distribution, material properties, process efficiency',
  'community-organizer': 'Focus on: networks, exchange patterns, distribution, gathering points',
  'formerly-incarcerated': 'Focus on: what thrives despite restriction, what endures, what transforms',
  scientist: 'Focus on: measurable processes, chemical exchanges, observable causality',
  student: 'Focus on: what you would write a paper about, what questions arise',
  custodian: 'Focus on: what is maintained, what decays, what the maintenance enables',
  default: 'Focus on: whatever draws your attention first',
};

const NAV_NODES = [
  { id: 'open-letter', label: 'Open Letter', desc: 'Deep Green — The Foundation Document. The letter this platform was built from.', href: '/open-letter', color: '#c9a84c', x: 50, y: 10 },
  { id: 'ayat-in-creation', label: 'Ayat in Creation', desc: 'Signs in the environment. The first lens.', href: '/ayat-in-creation', color: '#2d7a47', x: 85, y: 30 },
  { id: 'inner-reality-lens', label: 'Inner Reality Lens', desc: 'The recognition tool. Eight layers of observation.', href: '/inner-reality-lens', color: '#2d8a87', x: 90, y: 65 },
  { id: 'names-reference', label: 'Names Reference', desc: '99+ Names of Allah. Searchable, cross-linked.', href: '/names-reference', color: '#d4a82a', x: 65, y: 90 },
  { id: 'muslim-resource-guide', label: 'Muslim Resource Guide', desc: 'Local resources, caretaker roles, community networks.', href: '/muslim-resource-guide', color: '#2d7a47', x: 30, y: 90 },
  { id: 'bridge-irl-application', label: 'Bridge Pages', desc: 'Uboor — connecting recognition to action.', href: '/bridge-irl-application', color: '#5a5a8a', x: 10, y: 65 },
  { id: 'community', label: 'Community', desc: 'Early caretakers. Growing together.', href: '/community', color: '#c9a84c', x: 15, y: 30 },
];

// ─── CURSOR GLOW HOOK ─────────────────────────────────────────────────────────

function useCursorGlow(containerRef: React.RefObject<HTMLDivElement | null>) {
  const [pos, setPos] = useState({ x: 50, y: 50 });
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      setPos({ x: ((e.clientX - rect.left) / rect.width) * 100, y: ((e.clientY - rect.top) / rect.height) * 100 });
    };
    el.addEventListener('mousemove', handler);
    return () => el.removeEventListener('mousemove', handler);
  }, [containerRef]);
  return pos;
}

// ─── LINEAGE KEYWORD DETECTION ────────────────────────────────────────────────

function detectLineageFromObservation(text: string): { lineage: string; label: string; description: string } {
  const lower = text.toLowerCase();

  const botanicalKeywords = /tree|leaf|grass|plant|flower|soil|seed|root|branch|bark|moss|fern|shrub|garden|forest|bloom|petal|stem/;
  const waterKeywords = /puddle|rain|sky|reflection|river|drain|cloud|wet|moisture|stream|pond|dew|flood|creek|water/;
  const transitionKeywords = /sidewalk|crack|border|between|threshold|gap|edge|fence|boundary|transition|margin|curb|pavement/;
  const restorationKeywords = /broken|weed|vacant|graffiti|empty|neglected|overgrown|abandoned|decay|ruin|damage|worn|faded/;
  const innerKeywords = /feel|sense|remember|remind|wonder|why|notice|think|realize|struck|moved|curious|question/;

  if (botanicalKeywords.test(lower)) return {
    lineage: 'forest-keeper',
    label: 'Forest Keeper',
    description: 'Your first recognition was botanical — rooted in the living systems of creation. The Forest Keeper tends what grows, reads the language of plants, and finds the Names in the cycles of life.',
  };
  if (waterKeywords.test(lower)) return {
    lineage: 'water-guardian',
    label: 'Water Guardian',
    description: 'Your first recognition was of water — the most essential provision. The Water Guardian reads flow, reflection, and the movement of Ar-Rahman\'s mercy through the landscape.',
  };
  if (transitionKeywords.test(lower)) return {
    lineage: 'edge-walker',
    label: 'Edge Walker',
    description: 'Your first recognition was of a threshold — the place between. The Edge Walker inhabits margins, reads transitions, and finds meaning in the spaces where one thing becomes another.',
  };
  if (restorationKeywords.test(lower)) return {
    lineage: 'restoration-warrior',
    label: 'Restoration Warrior',
    description: 'Your first recognition was of what needs mending. The Restoration Warrior sees Fasad and moves toward Islah — recognizing the Names that were transgressed and working to restore the balance.',
  };
  if (innerKeywords.test(lower)) return {
    lineage: 'inner-seeker',
    label: 'Inner Seeker',
    description: 'Your first recognition turned inward — toward feeling, wondering, questioning. The Inner Seeker reads the Ayat within as much as without, finding the Names in the movements of the heart.',
  };

  return {
    lineage: 'explorer',
    label: 'Explorer',
    description: 'Your first recognition is open — all paths are available to you. The Explorer moves through the garden without a fixed orientation, following what calls to them.',
  };
}

// ─── FIRST ENCOUNTER PROMPT ───────────────────────────────────────────────────

function FirstEncounterPrompt({ onNext, onLineageSet }: { onNext: () => void; onLineageSet: (lineage: string) => void }) {
  const [text, setText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [detected, setDetected] = useState<{ lineage: string; label: string; description: string } | null>(null);
  const [resonates, setResonates] = useState<boolean | null>(null);
  const [focused, setFocused] = useState(false);

  const handleSubmit = () => {
    if (text.trim().length < 5) return;
    const result = detectLineageFromObservation(text);
    setDetected(result);
    setSubmitted(true);
  };

  const handleResonate = (yes: boolean) => {
    setResonates(yes);
    if (detected) {
      const lineageToSave = yes ? detected.lineage : 'explorer';
      if (typeof window !== 'undefined') {
        localStorage.setItem('haal_lineage', lineageToSave);
        localStorage.setItem('haal_lineage_recognized', 'true');
        localStorage.setItem('haal_first_encounter_text', text);
      }
      onLineageSet(lineageToSave);
    }
    setTimeout(() => onNext(), 1200);
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center"
      style={{ background: '#0a1a12', padding: '24px' }}
    >
      <div style={{ maxWidth: 560, width: '100%' }}>
        {!submitted ? (
          <>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <p
                style={{
                  fontFamily: 'monospace',
                  fontSize: 9,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: 'rgba(201,168,76,0.4)',
                  marginBottom: 12,
                }}
              >
                First Encounter · اللقاء الأول
              </p>
              <p
                style={{
                  fontFamily: "'Amiri', 'Georgia', serif",
                  fontSize: 'clamp(22px, 5vw, 32px)',
                  color: '#c9a84c',
                  lineHeight: 1.5,
                  marginBottom: 8,
                }}
              >
                Step outside. Look at one thing.
              </p>
              <p
                style={{
                  fontFamily: "'Amiri', 'Georgia', serif",
                  fontSize: 'clamp(18px, 4vw, 24px)',
                  color: 'rgba(201,168,76,0.7)',
                  lineHeight: 1.5,
                  marginBottom: 20,
                  fontStyle: 'italic',
                }}
              >
                What is the first thing you notice?
              </p>
              <p
                style={{
                  color: 'rgba(212,197,169,0.45)',
                  fontSize: 13,
                  lineHeight: 1.7,
                  maxWidth: 440,
                  margin: '0 auto',
                }}
              >
                Not what you think you should notice. What creation showed you first.
              </p>
            </div>

            {/* Text input */}
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              placeholder="A budding tree. A puddle reflecting sky. A crack in the pavement with a weed growing through it..."
              rows={4}
              style={{
                width: '100%',
                background: 'rgba(255,255,255,0.03)',
                border: `1px solid ${focused ? 'rgba(201,168,76,0.4)' : 'rgba(201,168,76,0.15)'}`,
                borderRadius: 10,
                padding: '16px 18px',
                color: '#d4c5a9',
                fontSize: 15,
                lineHeight: 1.7,
                fontFamily: "'Georgia', serif",
                resize: 'vertical',
                outline: 'none',
                transition: 'border-color 0.2s',
                marginBottom: 20,
                boxSizing: 'border-box',
              }}
            />

            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                onClick={handleSubmit}
                disabled={text.trim().length < 5}
                style={{
                  fontFamily: 'monospace',
                  fontSize: 11,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  padding: '14px 32px',
                  borderRadius: 8,
                  background: text.trim().length >= 5 ? 'rgba(201,168,76,0.1)' : 'transparent',
                  border: `1px solid ${text.trim().length >= 5 ? '#c9a84c' : 'rgba(201,168,76,0.2)'}`,
                  color: text.trim().length >= 5 ? '#c9a84c' : 'rgba(201,168,76,0.3)',
                  cursor: text.trim().length >= 5 ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s',
                }}
              >
                Submit →
              </button>
              <button
                onClick={onNext}
                style={{
                  fontFamily: 'monospace',
                  fontSize: 10,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  padding: '14px 20px',
                  borderRadius: 8,
                  background: 'transparent',
                  border: '1px solid rgba(212,197,169,0.1)',
                  color: 'rgba(212,197,169,0.3)',
                  cursor: 'pointer',
                }}
              >
                Skip
              </button>
            </div>

            <p
              style={{
                fontFamily: 'monospace',
                fontSize: 9,
                letterSpacing: '0.08em',
                color: 'rgba(212,197,169,0.2)',
                textAlign: 'center',
                marginTop: 16,
                lineHeight: 1.5,
              }}
            >
                &ldquo;What&apos;s being called to is Revelation. The calling precedes the caller.&rdquo;
                <br />— Open Letter to Imprisoned Caretakers
            </p>
          </>
        ) : detected && (
          <>
            {/* Result */}
            <div style={{ textAlign: 'center', marginBottom: 28 }}>
              <p
                style={{
                  fontFamily: 'monospace',
                  fontSize: 9,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: 'rgba(201,168,76,0.4)',
                  marginBottom: 12,
                }}
              >
                First Recognition
              </p>
              <div
                style={{
                  background: 'rgba(201,168,76,0.04)',
                  border: '1px solid rgba(201,168,76,0.15)',
                  borderRadius: 10,
                  padding: '14px 18px',
                  marginBottom: 20,
                }}
              >
                <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 13, fontStyle: 'italic', lineHeight: 1.6 }}>
                  &ldquo;{text}&rdquo;
                </p>
              </div>
              <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 13, lineHeight: 1.7, marginBottom: 8 }}>
                Your first recognition was of <em style={{ color: '#d4c5a9' }}>{text.split(' ').slice(0, 5).join(' ')}...</em>
              </p>
              <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 13, lineHeight: 1.7, marginBottom: 20 }}>
                This suggests a{' '}
                <strong style={{ color: '#c9a84c' }}>{detected.label}</strong>{' '}
                orientation.
              </p>
              <div
                style={{
                  background: 'rgba(45,122,71,0.06)',
                  border: '1px solid rgba(45,122,71,0.2)',
                  borderRadius: 10,
                  padding: '16px 18px',
                  marginBottom: 24,
                  textAlign: 'left',
                }}
              >
                <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 13, lineHeight: 1.75 }}>
                  {detected.description}
                </p>
              </div>
              <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 14, marginBottom: 20 }}>
                Does this resonate?
              </p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                <button
                  onClick={() => handleResonate(true)}
                  style={{
                    fontFamily: 'monospace',
                    fontSize: 10,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    padding: '12px 28px',
                    borderRadius: 8,
                    background: resonates === true ? 'rgba(45,122,71,0.15)' : 'rgba(45,122,71,0.06)',
                    border: '1px solid rgba(45,122,71,0.4)',
                    color: '#2d7a47',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  Yes, it does
                </button>
                <button
                  onClick={() => handleResonate(false)}
                  style={{
                    fontFamily: 'monospace',
                    fontSize: 10,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    padding: '12px 28px',
                    borderRadius: 8,
                    background: resonates === false ? 'rgba(201,168,76,0.1)' : 'transparent',
                    border: '1px solid rgba(201,168,76,0.25)',
                    color: '#c9a84c',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  Not quite
                </button>
              </div>
              <p
                style={{
                  fontFamily: 'monospace',
                  fontSize: 9,
                  color: 'rgba(212,197,169,0.25)',
                  marginTop: 16,
                  lineHeight: 1.5,
                }}
              >
                Recognized as {detected.label} — not selected. The calling precedes the caller.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── FITRA ORIENTATION QUESTION ───────────────────────────────────────────────

function FitraOrientationQuestion({ onNext }: { onNext: () => void }) {
  const [answered, setAnswered] = useState(false);
  const [answer, setAnswer] = useState<'yes' | 'pause' | null>(null);

  const handleAnswer = (a: 'yes' | 'pause') => {
    setAnswer(a);
    setAnswered(true);
    if (typeof window !== 'undefined') {
      localStorage.setItem('haal_fitra_orientation', a);
    }
    setTimeout(() => onNext(), 1800);
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center"
      style={{ background: '#060d12', padding: '24px' }}
    >
      <div style={{ maxWidth: 560, width: '100%', textAlign: 'center' }}>
        {/* Quran 30:30 */}
        <div style={{
          background: 'rgba(201,168,76,0.04)',
          border: '1px solid rgba(201,168,76,0.15)',
          borderLeft: '3px solid rgba(201,168,76,0.5)',
          borderRadius: '0 10px 10px 0',
          padding: '20px 24px',
          marginBottom: 32,
          textAlign: 'left',
        }}>
          <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 10 }}>
            QURAN 30:30 — THE FITRA VERSE
          </div>
          <p style={{
            fontFamily: "'Amiri', serif",
            fontSize: 18,
            color: '#c9a84c',
            direction: 'rtl',
            textAlign: 'right',
            lineHeight: 1.8,
            marginBottom: 12,
          }}>
            فَأَقِمْ وَجْهَكَ لِلدِّينِ حَنِيفًا ۚ فِطْرَتَ اللَّهِ الَّتِي فَطَرَ النَّاسَ عَلَيْهَا
          </p>
          <p style={{
            fontFamily: "'Georgia', serif",
            fontStyle: 'italic',
            fontSize: 13,
            color: '#d4c5a9',
            lineHeight: 1.8,
          }}>
            "So set your orientation to the way of life, upright, the original design of nature Allah made, which He has created mankind on it. No change in the creation Allah."
          </p>
        </div>

        {/* The question */}
        <div style={{ marginBottom: 32 }}>
          <p style={{ fontSize: 9, color: 'rgba(201,168,76,0.4)', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 16 }}>
            BEFORE YOU PLANT ANYTHING
          </p>
          <p style={{
            fontFamily: "'Amiri', 'Georgia', serif",
            fontSize: 'clamp(18px, 4vw, 24px)',
            color: '#d4c5a9',
            lineHeight: 1.65,
            marginBottom: 8,
          }}>
            Do you recognize that you were designed to see what you're about to be shown?
          </p>
          <p style={{ fontSize: 12, color: 'rgba(212,197,169,0.4)', lineHeight: 1.7, fontStyle: 'italic' }}>
            The Fitra is not something you earn. It is the original design upon which you were created. "No change in the creation of Allah."
          </p>
        </div>

        {/* Response options */}
        {!answered ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 380, margin: '0 auto' }}>
            <button
              onClick={() => handleAnswer('yes')}
              style={{
                fontFamily: "'Georgia', serif",
                fontSize: 14,
                color: '#2d7a47',
                background: 'rgba(45,122,71,0.06)',
                border: '1px solid rgba(45,122,71,0.3)',
                borderRadius: 8,
                padding: '16px 24px',
                cursor: 'pointer',
                lineHeight: 1.5,
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(45,122,71,0.12)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(45,122,71,0.06)'; }}
            >
              Yes — I recognize this.
            </button>
            <button
              onClick={() => handleAnswer('pause')}
              style={{
                fontFamily: "'Georgia', serif",
                fontSize: 14,
                color: 'rgba(212,197,169,0.6)',
                background: 'transparent',
                border: '1px solid rgba(212,197,169,0.12)',
                borderRadius: 8,
                padding: '16px 24px',
                cursor: 'pointer',
                lineHeight: 1.5,
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(212,197,169,0.04)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
            >
              I'm pausing with this question.
            </button>
          </div>
        ) : (
          <div style={{
            padding: '20px 24px',
            background: answer === 'yes' ? 'rgba(45,122,71,0.06)' : 'rgba(201,168,76,0.04)',
            border: `1px solid ${answer === 'yes' ? 'rgba(45,122,71,0.2)' : 'rgba(201,168,76,0.15)'}`,
            borderRadius: 8,
            animation: 'fadeIn 0.4s ease',
          }}>
            <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
            {answer === 'yes' ? (
              <p style={{ color: '#2d7a47', fontSize: 14, lineHeight: 1.7 }}>
                Alhamdulillah. The garden begins with what was already planted in you.
              </p>
            ) : (
              <p style={{ color: '#c9a84c', fontSize: 14, lineHeight: 1.7 }}>
                The pause is the recognition. The Fitra waits — it does not expire.
              </p>
            )}
          </div>
        )}

        <p style={{ fontSize: 9, color: 'rgba(212,197,169,0.2)', marginTop: 24, letterSpacing: '.08em' }}>
          This is the first Quranic verse in the Open Letter — the ground the walk begins on.
        </p>
      </div>
    </div>
  );
}

// ─── STEP COMPONENTS ──────────────────────────────────────────────────────────

function Step0({ onNext }: { onNext: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const pos = useCursorGlow(ref);
  return (
    <div
      ref={ref}
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
      style={{ background: '#0a1a12' }}
    >
      {/* Cursor glow */}
      <div
        className="pointer-events-none absolute inset-0 transition-all duration-700"
        style={{
          background: `radial-gradient(600px circle at ${pos.x}% ${pos.y}%, rgba(45,122,71,0.18) 0%, transparent 70%)`,
        }}
      />
      {/* Grain texture */}
      <div className="pointer-events-none absolute inset-0 opacity-20" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.4\'/%3E%3C/svg%3E")', backgroundSize: '200px 200px' }} />

      <div className="relative z-10 flex flex-col items-center text-center px-6 max-w-2xl mx-auto">
        {/* Bismillah */}
        <p
          className="mb-3 animate-fade-in"
          style={{ fontFamily: "'Amiri', 'Georgia', serif", fontSize: 32, color: '#c9a84c', direction: 'rtl', animationDelay: '0.2s' }}
        >
          بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ
        </p>
        <p className="mb-6 italic" style={{ color: '#d4c5a9', fontSize: 14 }}>
          In the name of Allah, The Most Compassionate, The Most Kind
        </p>

        {/* Gold rule */}
        <div className="w-24 h-px mb-6" style={{ background: 'rgba(201,168,76,0.4)' }} />

        <p className="font-mono uppercase tracking-widest mb-2" style={{ color: 'rgba(201,168,76,0.5)', fontSize: 11 }}>Welcome to</p>

        {/* Platform name */}
        <h1
          className="mb-4"
          style={{
            fontFamily: "'Amiri', 'Georgia', serif",
            fontSize: 'clamp(36px, 8vw, 56px)',
            color: '#c9a84c',
            letterSpacing: '0.08em',
            animation: 'goldShimmer 3s ease-in-out infinite',
          }}
        >
          THE HAAL PROJECT
        </h1>

        <p className="italic mb-2" style={{ color: '#d4c5a9', fontSize: 16 }}>
          Keys to Kulli Haal — كل حال وكل شيء
        </p>
        <p className="mb-1" style={{ color: 'rgba(212,197,169,0.5)', fontSize: 13 }}>
          All praise and thanks are due to Allah, every thing and every state.
        </p>
        <p className="font-mono uppercase tracking-widest mb-8" style={{ color: 'rgba(212,197,169,0.35)', fontSize: 11, letterSpacing: '0.2em' }}>
          An open letter to caretakers.
        </p>

        {/* Orientation statement — the key addition */}
        <div
          className="mb-10 rounded-xl px-7 py-6 text-left"
          style={{
            background: 'rgba(45,122,71,0.06)',
            border: '1px solid rgba(45,122,71,0.18)',
            borderLeft: '3px solid rgba(201,168,76,0.4)',
            maxWidth: 520,
          }}
        >
          <p className="font-mono uppercase tracking-widest mb-3" style={{ color: 'rgba(201,168,76,0.5)', fontSize: 9 }}>
            BEFORE YOU BEGIN · قبل أن تبدأ
          </p>
          <p style={{ color: '#d4c5a9', fontSize: 14, lineHeight: 1.85, marginBottom: 12 }}>
            This is not an app. It is a methodology for seeing creation differently — a structured practice of recognition rooted in the Names and Attributes of Allah.
          </p>
          <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 13, lineHeight: 1.8, marginBottom: 12 }}>
            You are already a caretaker. You already carry the Amanah — the trust of creation — in your daily work, your relationships, your observations of the world around you. This platform gives that work a vocabulary, a framework, and a community.
          </p>
          <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12, lineHeight: 1.75, fontStyle: 'italic' }}>
            What follows is not a tutorial. It is an orientation — a first encounter with a way of seeing that has always been available to you.
          </p>
          <div className="mt-4 pt-4 flex items-center gap-3" style={{ borderTop: '1px solid rgba(201,168,76,0.1)' }}>
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#2d7a47', flexShrink: 0 }} />
            <p className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'rgba(45,122,71,0.7)' }}>
              5 steps · 10 minutes · A lifetime of practice
            </p>
          </div>
        </div>

        <button
          onClick={onNext}
          className="font-mono uppercase tracking-widest px-10 py-4 transition-all duration-300"
          style={{
            border: '1px solid #c9a84c',
            color: '#c9a84c',
            background: 'transparent',
            fontSize: 13,
            letterSpacing: '0.15em',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(201,168,76,0.1)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
        >
          Begin →
        </button>
      </div>

      <style>{`
        @keyframes goldShimmer {
          0%, 100% { opacity: 1; text-shadow: 0 0 20px rgba(201,168,76,0.3); }
          50% { opacity: 0.85; text-shadow: 0 0 40px rgba(201,168,76,0.6); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fadeIn 0.8s ease forwards; }
      `}</style>
    </div>
  );
}

function Step1({ onNext }: { onNext: () => void }) {
  const [visible, setVisible] = useState<number[]>([]);
  useEffect(() => {
    FIVE_HAALS.forEach((_, i) => {
      setTimeout(() => setVisible(prev => [...prev, i]), i * 150 + 200);
    });
  }, []);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0a1a12' }}>
      <div className="flex-1 max-w-5xl mx-auto w-full px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="font-mono uppercase tracking-widest mb-3" style={{ color: '#c9a84c', fontSize: 'clamp(14px, 3vw, 20px)', letterSpacing: '0.2em' }}>
            THE FIVE HAALS · خمسة أحوال
          </h2>
          <p style={{ color: '#d4c5a9', fontSize: 15, fontStyle: 'italic' }}>
            Every caretaker moves through five states of recognition and action.
          </p>
        </div>

        {/* Cards — horizontal scroll desktop, vertical mobile */}
        <div className="flex flex-col lg:flex-row gap-4 overflow-x-auto pb-4">
          {FIVE_HAALS.map((h, i) => (
            <div
              key={i}
              className="flex-shrink-0 lg:flex-1 transition-all duration-500"
              style={{
                opacity: visible.includes(i) ? 1 : 0,
                transform: visible.includes(i) ? 'translateY(0)' : 'translateY(20px)',
                minWidth: 220,
              }}
            >
              <div
                className="h-full rounded-lg p-5"
                style={{
                  background: 'rgba(6,13,18,0.7)',
                  border: `1px solid ${h.color}40`,
                  borderTop: `3px solid ${h.color}`,
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <span style={{ fontFamily: "'Amiri', serif", fontSize: 28, color: h.color, lineHeight: 1 }}>{h.num}</span>
                  <span className="font-mono text-[9px] tracking-widest uppercase px-2 py-0.5 rounded" style={{ border: `1px solid ${h.color}40`, color: h.color }}>
                    {h.subtitle.split(':')[0]}
                  </span>
                </div>
                <p style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: h.color, direction: 'rtl', marginBottom: 4 }}>{h.arabic}</p>
                <h3 className="font-mono uppercase tracking-wide mb-3" style={{ color: '#d4c5a9', fontSize: 11 }}>{h.title}</h3>
                <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 12, lineHeight: 1.6, marginBottom: 12 }}>{h.description}</p>
                <div className="pt-3" style={{ borderTop: `1px solid ${h.color}20` }}>
                  <p className="font-mono text-[9px] tracking-widest uppercase mb-1" style={{ color: h.color }}>PLATFORM</p>
                  <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11 }}>{h.platform}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 13, fontStyle: 'italic', marginBottom: 8 }}>
            These five haals compose the recognition arc: Encounter → Haqq → Amanah. They are the structure of the Inner Reality Lens.
          </p>
          <Link href="/inner-reality-lens" className="font-mono text-xs tracking-widest" style={{ color: '#2d8a87' }}>
            → Explore in the IRL Lens
          </Link>
        </div>

        <div className="flex justify-center mt-10">
          <button
            onClick={onNext}
            className="font-mono uppercase tracking-widest px-10 py-4 transition-all duration-300"
            style={{ border: '1px solid #c9a84c', color: '#c9a84c', background: 'transparent', fontSize: 13, letterSpacing: '0.15em' }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(201,168,76,0.1)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            Continue →
          </button>
        </div>
      </div>
    </div>
  );
}

function Step2({ onNext, selectedRole, setSelectedRole }: { onNext: () => void; selectedRole: string | null; setSelectedRole: (r: string) => void }) {
  const handleSelect = (id: string) => {
    setSelectedRole(id);
    if (typeof window !== 'undefined') {
      localStorage.setItem('haal_role', id);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0a1a12' }}>
      <div className="flex-1 max-w-4xl mx-auto w-full px-6 py-16">
        <div className="text-center mb-10">
          <h2 className="font-mono uppercase tracking-widest mb-3" style={{ color: '#c9a84c', fontSize: 'clamp(14px, 3vw, 20px)', letterSpacing: '0.2em' }}>
            YOUR CARETAKER ROLE · دورك
          </h2>
          <p style={{ color: '#d4c5a9', fontSize: 15, fontStyle: 'italic', maxWidth: 560, margin: '0 auto' }}>
            Every role has unique work, unique Ayat to observe, and unique Amanah to fulfill. Select the role that resonates most with your current context — you can explore others later.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
          {ROLES.map(role => {
            const isSelected = selectedRole === role.id;
            return (
              <button
                key={role.id}
                onClick={() => handleSelect(role.id)}
                className="text-left rounded-lg p-4 transition-all duration-200"
                style={{
                  background: isSelected ? 'rgba(201,168,76,0.08)' : 'rgba(6,13,18,0.6)',
                  border: isSelected ? '1px solid #c9a84c' : '1px solid rgba(201,168,76,0.15)',
                }}
              >
                <div className="font-mono uppercase tracking-wide mb-2" style={{ color: isSelected ? '#c9a84c' : '#d4c5a9', fontSize: 11 }}>
                  {role.label}
                </div>
                <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11, lineHeight: 1.5, marginBottom: 8 }}>
                  {role.question}
                </p>
                <div className="flex flex-wrap gap-1">
                  {role.keys.split(', ').map(k => (
                    <span key={k} className="font-mono text-[8px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(45,138,135,0.1)', border: '1px solid rgba(45,138,135,0.2)', color: '#2d8a87' }}>
                      {k}
                    </span>
                  ))}
                </div>
              </button>
            );
          })}
        </div>

        <p className="text-center mb-8" style={{ color: 'rgba(212,197,169,0.4)', fontSize: 12, fontStyle: 'italic' }}>
          Your role shapes which IRL tiles are highlighted, which MRG resources surface first, and which caretaker quests you receive.
        </p>

        <div className="flex flex-col items-center gap-3">
          <button
            onClick={onNext}
            disabled={!selectedRole}
            className="font-mono uppercase tracking-widest px-10 py-4 transition-all duration-300"
            style={{
              border: `1px solid ${selectedRole ? '#c9a84c' : 'rgba(201,168,76,0.3)'}`,
              color: selectedRole ? '#c9a84c' : 'rgba(201,168,76,0.3)',
              background: 'transparent',
              fontSize: 13,
              letterSpacing: '0.15em',
              cursor: selectedRole ? 'pointer' : 'not-allowed',
            }}
          >
            Continue →
          </button>
          <button
            onClick={onNext}
            className="font-mono text-xs tracking-widest transition-colors"
            style={{ color: 'rgba(212,197,169,0.35)', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            I&apos;ll choose later →
          </button>
        </div>
      </div>
    </div>
  );
}

function Step3({ onNext, selectedRole }: { onNext: () => void; selectedRole: string | null }) {
  const router = useRouter();
  const focusText = ROLE_QUEST_FOCUS[selectedRole || 'default'] || ROLE_QUEST_FOCUS.default;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: '#0a1a12' }}>
      <div className="w-full max-w-2xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <h2 className="font-mono uppercase tracking-widest mb-3" style={{ color: '#c9a84c', fontSize: 'clamp(14px, 3vw, 20px)', letterSpacing: '0.2em' }}>
            YOUR FIRST QUEST · أول مهمة
          </h2>
          <p style={{ color: '#d4c5a9', fontSize: 15, fontStyle: 'italic' }}>Keys to Hayy — Life: The Living Network</p>
        </div>

        {/* Quest card */}
        <div
          className="rounded-xl mb-8"
          style={{
            background: 'rgba(6,13,18,0.8)',
            border: '1px solid rgba(201,168,76,0.3)',
            padding: 'clamp(24px, 5vw, 48px)',
          }}
        >
          <div className="font-mono text-[9px] tracking-widest uppercase mb-4" style={{ color: '#2d8a87' }}>
            QUEST 01 · مهمة أولى
          </div>
          <h3 style={{ fontFamily: "'Amiri', 'Georgia', serif", fontSize: 32, color: '#c9a84c', marginBottom: 12 }}>
            Observe Five Signs
          </h3>
          <p style={{ fontFamily: "'Amiri', serif", fontSize: 20, color: 'rgba(201,168,76,0.7)', direction: 'rtl', marginBottom: 6 }}>
            وَفِي الْأَرْضِ آيَاتٌ لِّلْمُوقِنِينَ
          </p>
          <p className="italic mb-8" style={{ color: '#d4c5a9', fontSize: 14 }}>
            &ldquo;And in the earth are signs for those who are certain.&rdquo; — (Quran 51:20)
          </p>

          <div className="mb-6" style={{ color: 'rgba(212,197,169,0.8)', fontSize: 13, lineHeight: 1.8 }}>
            <p className="mb-3">Step outside or look around your current environment. Observe five things — a plant, an animal, a material, a process, a community encounter. For each, ask:</p>
            <ol className="list-none space-y-2 pl-4">
              <li><span style={{ color: '#c9a84c' }}>1. IS IT?</span> — does it exist?</li>
              <li><span style={{ color: '#2d8a87' }}>2. WHAT IS IT?</span> — what are its essential qualities?</li>
              <li><span style={{ color: '#2d7a47' }}>3. WHEN IS/WAS IT?</span> — what sequence brought it here?</li>
            </ol>
          </div>

          <p className="mb-4" style={{ color: 'rgba(212,197,169,0.6)', fontSize: 13 }}>
            Record your observations in the IRL Lens. Each observation unlocks a Names sequence.
          </p>

          {/* Role-specific focus */}
          <div className="rounded-lg p-4" style={{ background: 'rgba(45,122,71,0.08)', border: '1px solid rgba(45,122,71,0.2)' }}>
            <p className="font-mono text-[9px] tracking-widest uppercase mb-2" style={{ color: '#2d7a47' }}>
              {selectedRole ? `FOR ${selectedRole.toUpperCase().replace('-', ' ')}:` : 'YOUR FOCUS:'}
            </p>
            <p style={{ color: '#d4c5a9', fontSize: 12, lineHeight: 1.6 }}>{focusText}</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => router.push('/inner-reality-lens')}
            className="font-mono uppercase tracking-widest px-8 py-4 transition-all duration-300"
            style={{ background: '#c9a84c', color: '#0a1a12', fontSize: 12, letterSpacing: '0.15em', border: 'none', cursor: 'pointer' }}
          >
            Open IRL Lens →
          </button>
          <button
            onClick={onNext}
            className="font-mono uppercase tracking-widest px-8 py-4 transition-all duration-300"
            style={{ border: '1px solid rgba(201,168,76,0.4)', color: '#c9a84c', background: 'transparent', fontSize: 12, letterSpacing: '0.15em', cursor: 'pointer' }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(201,168,76,0.08)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            Continue to Platform Map →
          </button>
        </div>
      </div>
    </div>
  );
}

function Step4({ onNext, selectedRole }: { onNext: () => void; selectedRole: string | null }) {
  const router = useRouter();

  // Role-based highlights
  const roleHighlights: Record<string, string[]> = {
    engineer: ['inner-reality-lens', 'bridge-irl-application'],
    teacher: ['keys-to-kulli-haal', 'open-letter'],
    scientist: ['ayat-in-creation', 'inner-reality-lens'],
    gardener: ['ayat-in-creation', 'inner-reality-lens'],
    'community-organizer': ['muslim-resource-guide', 'community'],
    'formerly-incarcerated': ['muslim-resource-guide', 'bridge-irl-application'],
    student: ['open-letter', 'names-reference'],
    custodian: ['inner-reality-lens', 'muslim-resource-guide'],
  };
  const highlighted = selectedRole ? (roleHighlights[selectedRole] || []) : [];

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0a1a12' }}>
      <div className="flex-1 max-w-5xl mx-auto w-full px-6 py-16">
        <div className="text-center mb-10">
          <h2 className="font-mono uppercase tracking-widest mb-3" style={{ color: '#c9a84c', fontSize: 'clamp(14px, 3vw, 20px)', letterSpacing: '0.2em' }}>
            THE HAAL PROJECT · خريطة المشروع
          </h2>
          <p style={{ color: '#d4c5a9', fontSize: 15, fontStyle: 'italic' }}>Eight interconnected spaces. One journey.</p>
        </div>

        {/* Visual map */}
        <div className="relative rounded-xl mb-8 overflow-hidden" style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(201,168,76,0.15)', minHeight: 400, padding: 32 }}>
          {/* Central node */}
          <div className="absolute" style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 10 }}>
            <div className="rounded-full flex items-center justify-center text-center" style={{ width: 100, height: 100, background: 'rgba(201,168,76,0.12)', border: '2px solid rgba(201,168,76,0.5)' }}>
              <div>
                <p className="font-mono text-[8px] tracking-widest uppercase" style={{ color: '#c9a84c' }}>Keys to</p>
                <p style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: '#c9a84c' }}>كل حال</p>
              </div>
            </div>
          </div>

          {/* Surrounding nodes */}
          {NAV_NODES.map(node => {
            const isHighlighted = highlighted.includes(node.id);
            return (
              <div
                key={node.id}
                className="absolute"
                style={{ left: `${node.x}%`, top: `${node.y}%`, transform: 'translate(-50%, -50%)', zIndex: 5 }}
              >
                <Link href={node.href}>
                  <div
                    className="rounded-lg p-3 text-center transition-all duration-300 cursor-pointer"
                    style={{
                      background: isHighlighted ? `${node.color}18` : 'rgba(6,13,18,0.9)',
                      border: `1px solid ${isHighlighted ? node.color : node.color + '40'}`,
                      boxShadow: isHighlighted ? `0 0 16px ${node.color}30` : 'none',
                      minWidth: 110,
                      maxWidth: 130,
                    }}
                  >
                    <p className="font-mono text-[9px] tracking-widest uppercase" style={{ color: node.color, lineHeight: 1.3 }}>{node.label}</p>
                    <p className="hidden sm:block mt-1" style={{ color: 'rgba(212,197,169,0.5)', fontSize: 9, lineHeight: 1.4 }}>{node.desc.slice(0, 50)}</p>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => router.push('/inner-reality-lens')}
            className="font-mono uppercase tracking-widest px-8 py-4 transition-all duration-300"
            style={{ background: '#c9a84c', color: '#0a1a12', fontSize: 12, letterSpacing: '0.15em', border: 'none', cursor: 'pointer' }}
          >
            Enter the Platform →
          </button>
          <button
            onClick={onNext}
            className="font-mono uppercase tracking-widest px-8 py-4 transition-all duration-300"
            style={{ border: '1px solid rgba(201,168,76,0.4)', color: '#c9a84c', background: 'transparent', fontSize: 12, letterSpacing: '0.15em', cursor: 'pointer' }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(201,168,76,0.08)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            Join the Community →
          </button>
        </div>
      </div>
    </div>
  );
}

function Step5({ selectedRole }: { selectedRole: string | null }) {
  const router = useRouter();
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', city: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email) return;
    setLoading(true);
    try {
      const { supabase } = await import('@/lib/supabaseClient');
      await supabase.from('community_signups').insert([{
        name: form.name,
        email: form.email,
        role: selectedRole || 'default',
        city: form.city,
        source: 'guided-intro',
      }]);
      setSubmitted(true);
    } catch {
      setSubmitted(true); // Show success even on error for UX
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6" style={{ background: '#0a1a12' }}>
        <div className="max-w-md w-full text-center rounded-xl p-10" style={{ background: 'rgba(45,122,71,0.08)', border: '1px solid rgba(45,122,71,0.3)' }}>
          <p style={{ fontFamily: "'Amiri', serif", fontSize: 28, color: '#c9a84c', marginBottom: 8 }}>الحمد لله</p>
          <h3 className="font-mono uppercase tracking-widest mb-4" style={{ color: '#d4c5a9', fontSize: 14 }}>Alhamdulilah. You&apos;re in.</h3>
          <p className="mb-8" style={{ color: 'rgba(212,197,169,0.6)', fontSize: 13, lineHeight: 1.7 }}>
            We&apos;ll be in touch as the platform grows. In the meantime, explore:
          </p>
          <div className="flex flex-col gap-3">
            <Link href="/inner-reality-lens" className="font-mono text-xs tracking-widest py-3 px-6 text-center transition-all" style={{ border: '1px solid rgba(45,138,135,0.4)', color: '#2d8a87' }}>IRL Lens →</Link>
            <Link href="/open-letter" className="font-mono text-xs tracking-widest py-3 px-6 text-center transition-all" style={{ border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c' }}>Open Letter →</Link>
            <Link href="/names-reference" className="font-mono text-xs tracking-widest py-3 px-6 text-center transition-all" style={{ border: '1px solid rgba(212,197,169,0.2)', color: '#d4c5a9' }}>Names Reference →</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6" style={{ background: '#0a1a12' }}>
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h2 className="font-mono uppercase tracking-widest mb-3" style={{ color: '#c9a84c', fontSize: 16, letterSpacing: '0.2em' }}>
            JOIN THE CARETAKERS · انضم
          </h2>
          <p style={{ color: '#d4c5a9', fontSize: 14, fontStyle: 'italic' }}>
            Get updates as the platform grows.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="rounded-xl p-8 flex flex-col gap-4" style={{ background: 'rgba(6,13,18,0.7)', border: '1px solid rgba(201,168,76,0.2)' }}>
          <input
            type="text"
            placeholder="Your name"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            className="w-full px-4 py-3 font-mono text-sm outline-none"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(201,168,76,0.15)', color: '#d4c5a9', borderRadius: 4 }}
          />
          <input
            type="email"
            placeholder="your@email.com"
            value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            required
            className="w-full px-4 py-3 font-mono text-sm outline-none"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(201,168,76,0.15)', color: '#d4c5a9', borderRadius: 4 }}
          />
          <input
            type="text"
            placeholder="Your city"
            value={form.city}
            onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
            className="w-full px-4 py-3 font-mono text-sm outline-none"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(201,168,76,0.15)', color: '#d4c5a9', borderRadius: 4 }}
          />
          {selectedRole && (
            <div className="px-4 py-3 font-mono text-xs" style={{ background: 'rgba(45,122,71,0.08)', border: '1px solid rgba(45,122,71,0.2)', color: '#2d7a47', borderRadius: 4 }}>
              Role: {ROLES.find(r => r.id === selectedRole)?.label || selectedRole}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="font-mono uppercase tracking-widest py-4 transition-all duration-300"
            style={{ background: '#c9a84c', color: '#0a1a12', fontSize: 12, letterSpacing: '0.15em', border: 'none', cursor: loading ? 'wait' : 'pointer', borderRadius: 4 }}
          >
            {loading ? 'Joining...' : 'Join →'}
          </button>
        </form>

        <div className="text-center mt-6">
          <button onClick={() => router.push('/homepage')} className="font-mono text-xs tracking-widest" style={{ color: 'rgba(212,197,169,0.3)', background: 'none', border: 'none', cursor: 'pointer' }}>
            Skip → Go to Homepage
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function GuidedIntroPage() {
  const router = useRouter();
  const [step, setStep] = useState(0);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [detectedLineage, setDetectedLineage] = useState<string | null>(null);

  // Load saved role from localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('haal_role');
      if (saved) setSelectedRole(saved);
      const savedLineage = localStorage.getItem('haal_lineage');
      if (savedLineage) setDetectedLineage(savedLineage);
    }
  }, []);

  // Steps: 0=Bismillah, 1=FitraOrientation, 2=FirstEncounter, 3=FiveHaals, 4=Role, 5=Quest, 6=Map, 7=Join
  const TOTAL_STEPS = 8;

  return (
    <div className="relative min-h-screen" style={{ background: '#0a1a12' }}>
      {/* Skip button */}
      <button
        onClick={() => router.push('/homepage')}
        className="fixed top-5 right-5 z-50 font-mono text-xs tracking-widest px-4 py-2 transition-all"
        style={{ border: '1px solid rgba(201,168,76,0.2)', color: 'rgba(201,168,76,0.4)', background: 'rgba(6,13,18,0.8)', borderRadius: 4 }}
      >
        Skip
      </button>

      {/* Step content */}
      <div className="transition-opacity duration-300">
        {step === 0 && <Step0 onNext={() => setStep(1)} />}
        {step === 1 && (
          <FitraOrientationQuestion
            onNext={() => setStep(2)}
          />
        )}
        {step === 2 && (
          <FirstEncounterPrompt
            onNext={() => setStep(3)}
            onLineageSet={(l) => setDetectedLineage(l)}
          />
        )}
        {step === 3 && <Step1 onNext={() => setStep(4)} />}
        {step === 4 && <Step2 onNext={() => setStep(5)} selectedRole={selectedRole} setSelectedRole={setSelectedRole} />}
        {step === 5 && <Step3 onNext={() => setStep(6)} selectedRole={selectedRole} />}
        {step === 6 && <Step4 onNext={() => setStep(7)} selectedRole={selectedRole} />}
        {step === 7 && <Step5 selectedRole={selectedRole} />}
      </div>

      {/* Progress dots */}
      {step > 0 && step < 7 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2">
          {Array.from({ length: TOTAL_STEPS - 1 }).map((_, i) => (
            <button
              key={i}
              onClick={() => setStep(i + 1)}
              className="rounded-full transition-all duration-300"
              style={{
                width: step === i + 1 ? 20 : 8,
                height: 8,
                background: step === i + 1 ? '#c9a84c' : step > i + 1 ? 'rgba(201,168,76,0.4)' : 'rgba(201,168,76,0.15)',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
